import * as React from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { getEntityCheckInPolicy, useGetEntityCheckInPolicy, getGetEntityCheckInPolicyQueryKey, type EntityCheckInPolicy } from '@cdf/api-client-react';
import { GetEntityCheckInPolicy200Response } from '@cdf/api-zod';
import { Button } from '@/components/ui/button';

export function isCheckInSurfaceQuery(key: QueryKey): boolean {
  const first = String(key[0] ?? '');
  return /^\/api\/(bookings|availability|presence|colleagues|workgroups)(\/|$)/.test(first) ||
    /^\/api\/(floors|spaces)\/[^/]+\/(bookings|availability|effective-policy|twin-status)$/.test(first) ||
    /^(booking-|availability|floor-bookings|colleague-desks|hold-claimed-booking|hold-claim-policy|desk-hold|twin-)/.test(first);
}
export async function resetCheckInSurfaces(client: QueryClient): Promise<void> {
  const filter = { predicate: (query: { queryKey: QueryKey }) => isCheckInSurfaceQuery(query.queryKey) };
  await client.cancelQueries(filter);
  await client.resetQueries(filter);
}
export async function refreshCheckInPolicies(client: QueryClient): Promise<void> {
  const filter = { predicate: (query: { queryKey: QueryKey }) => String(query.queryKey[0]).endsWith('/check-in-policy') };
  await client.cancelQueries(filter);
  await client.resetQueries(filter);
}

export interface CheckInPolicyState {
  ready: boolean;
  enabled: boolean;
  policy: EntityCheckInPolicy | null;
  key: string;
  error: boolean;
  refreshing: boolean;
  retry: () => Promise<unknown>;
  setSaving: (saving: boolean) => void;
  assertCurrent: () => void;
}
const unavailable: CheckInPolicyState = {
  ready: false, enabled: false, policy: null, key: 'unavailable', error: false, refreshing: false,
  retry: async () => undefined, setSaving: () => undefined,
  assertCurrent: () => { throw new Error('Check-in settings are unavailable. Reload the settings and try again.'); }
};
const Context = React.createContext<CheckInPolicyState>(unavailable);
export function useTenantCheckIn() { return React.useContext(Context); }

/** One observer for the selected entity; a policy revision never reuses old booking obligations. */
export function TenantCheckInProvider({ tenantSlug, entityId, tenantId, authority, active, children }: {
  tenantSlug: string; entityId?: string; tenantId?: string; authority: string; active: boolean; children: React.ReactNode;
}) {
  const client = useQueryClient();
  const scope = JSON.stringify([tenantSlug, entityId, tenantId, authority, active]);
  const current = React.useRef(scope);
  current.current = scope;
  const [savingScope, setSavingScope] = React.useState<string | null>(null);
  const saving = savingScope === scope;
  const queryKey = [...getGetEntityCheckInPolicyQueryKey(tenantSlug), scope];
  const query = useGetEntityCheckInPolicy(tenantSlug, {
    request: { headers: { 'X-Tenant-Slug': tenantSlug } },
    query: { queryKey,
      enabled: active && Boolean(tenantSlug && entityId && tenantId), retry: false, staleTime: 0,
      refetchOnWindowFocus: true, refetchInterval: 30_000,
      queryFn: async ({ signal }) => {
        const assertContext = () => { if (signal.aborted || current.current !== scope) throw new Error('Check-in context changed.'); };
        assertContext();
        const value = GetEntityCheckInPolicy200Response.parse(await getEntityCheckInPolicy(tenantSlug, {
          headers: { 'X-Tenant-Slug': tenantSlug }, signal, respectAbortSignal: true, assertContext
        }));
        assertContext();
        if (value.entityId !== entityId || value.tenantId !== tenantId) throw new Error('Check-in settings belong to another entity.');
        return value;
      }
    }
  });
  const policy = active && query.data?.entityId === entityId && query.data?.tenantId === tenantId ? query.data : null;
  const version = JSON.stringify([scope, policy?.revision, policy?.enabled, policy?.canManage]);
  const [synchronized, setSynchronized] = React.useState<string | null>(null);
  React.useLayoutEffect(() => {
    let retired = false;
    void resetCheckInSurfaces(client).then(() => { if (!retired) setSynchronized(version); });
    return () => { retired = true; };
  }, [client, version]);
  const ready = Boolean(policy && query.isSuccess && !query.isFetching && !query.isPlaceholderData && !saving && synchronized === version);
  const key = JSON.stringify([version, ready, query.dataUpdatedAt]);
  const gate = React.useRef({ scope, key, ready });
  gate.current = { scope, key, ready };
  const value: CheckInPolicyState = {
    ready, enabled: ready && policy!.enabled, policy: ready ? policy ?? null : null, key,
    error: query.isError, refreshing: query.isFetching,
    retry: () => { gate.current.ready = false; return query.refetch(); },
    setSaving: next => { if (current.current === scope) { if (next) gate.current.ready = false; setSavingScope(next ? scope : null); } },
    assertCurrent: () => {
      const currentQuery = client.getQueryState(queryKey);
      if (!ready || !policy?.enabled || gate.current.scope !== scope || gate.current.key !== key || !gate.current.ready ||
        currentQuery?.fetchStatus !== 'idle' || currentQuery.dataUpdatedAt !== query.dataUpdatedAt)
        throw new Error('Check-in settings changed or are unavailable. Reload and review this booking.');
    }
  };
  React.useLayoutEffect(() => {
    const value = { scope, key, ready };
    gate.current = value;
    return () => { if (gate.current === value) gate.current = { scope: '', key: '', ready: false }; };
  }, [scope, key, ready]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** Only arrival features wait for this read; cancellation, checkout and reservations stay available. */
export function CheckInPolicyNotice() {
  const state = useTenantCheckIn();
  if (state.ready) return null;
  return <div role={state.error ? 'alert' : 'status'} className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
    <p>{state.error ? 'Check-in settings could not load. Check-in and takeover are unavailable.' : 'Checking check-in settings…'}</p>
    {state.error ? <Button type="button" size="sm" variant="outline" disabled={state.refreshing} onClick={() => void state.retry()}>Retry check-in settings</Button> : null}
  </div>;
}
